// DeliveryPanel.jsx
// ─────────────────────────────────────────────────────────────
// Shows delivery performance summary for the active year:
//  - On time count + overdue + unknown
//  - Average remaining days (on-time only)
//  - Overdue PO detail list
// ─────────────────────────────────────────────────────────────
import styles from "./SourcingIntelligence.module.css";
import { Icons } from "./SourcingIntelligence";

export default function DeliveryPanel({ yearData }) {
  const items = yearData?.itemwise || [];

  const onTime  = items.filter(i => i.delivery_status === "on_time");
  const overdue = items.filter(i => i.delivery_status === "overdue");
  const unknown = items.filter(i => i.delivery_status === "unknown");

  const avgRemainingOnTime = onTime.length > 0
    ? Math.round(onTime.filter(i => i.remaining_delivery_days !== null)
        .reduce((s, i) => s + i.remaining_delivery_days, 0) / onTime.length)
    : null;

  const avgOverdueDays = overdue.length > 0
    ? Math.round(Math.abs(
        overdue.filter(i => i.remaining_delivery_days !== null)
          .reduce((s, i) => s + i.remaining_delivery_days, 0) / overdue.length
      ))
    : null;

  return (
    <>
      <div className={styles.sectionHeader} style={{ marginTop: 0 }}>
        <div className={styles.sectionTitle}>
          <div className={styles.sectionDot} />
          Delivery Performance — {yearData.year}
        </div>
      </div>

      <div className={styles.deliveryGrid}>
        {/* On Time */}
        <div className={styles.deliveryCard}>
          <div className={`${styles.deliveryIcon} ${styles.deliveryIconSuccess}`}>
            <Icons.CheckCircle size={22} />
          </div>
          <div>
            <div className={`${styles.deliveryCardVal} ${styles.deliveryValSuccess}`}>
              {onTime.length}
            </div>
            <div className={styles.deliveryCardLbl}>On-Time Deliveries</div>
            {avgRemainingOnTime !== null && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                <Icons.Clock size={12} />
                Avg {avgRemainingOnTime} days remaining
              </div>
            )}
          </div>
        </div>

        {/* Overdue */}
        <div className={styles.deliveryCard}>
          <div className={`${styles.deliveryIcon} ${styles.deliveryIconDanger}`}>
            <Icons.AlertCircle size={22} />
          </div>
          <div>
            <div className={`${styles.deliveryCardVal} ${styles.deliveryValDanger}`}>
              {overdue.length}
            </div>
            <div className={styles.deliveryCardLbl}>Overdue</div>
            {avgOverdueDays !== null && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                <Icons.Clock size={12} />
                Avg {avgOverdueDays} days late
              </div>
            )}
          </div>
        </div>

        {/* Unknown */}
        <div className={styles.deliveryCard}>
          <div className={`${styles.deliveryIcon} ${styles.deliveryIconNeutral}`}>
            <Icons.HelpCircle size={22} />
          </div>
          <div>
            <div className={`${styles.deliveryCardVal} ${styles.deliveryValNeutral}`}>
              {unknown.length}
            </div>
            <div className={styles.deliveryCardLbl}>Status Unknown</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
              No delivery date set
            </div>
          </div>
        </div>
      </div>

      {/* Overdue detail list */}
      {overdue.length > 0 && (
        <div style={{
          background: "var(--danger-bg)",
          border: "1.5px solid var(--danger-border)",
          borderRadius: "var(--radius)",
          padding: "16px 20px",
          marginBottom: 18,
        }}>
          <div style={{
            fontSize: 13, fontWeight: 800, color: "var(--danger)",
            marginBottom: 12, display: "flex", alignItems: "center", gap: 7,
          }}>
            <Icons.AlertCircle size={14} />
            Overdue POs — Require Attention
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {overdue.map(item => (
              <div key={item.po_number} style={{
                background: "#fff",
                border: "1px solid var(--danger-border)",
                borderRadius: "var(--radius-sm)",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 10,
              }}>
                <div>
                  <span style={{ fontWeight: 800, color: "var(--primary)", fontSize: 14 }}>
                    PO# {item.po_number}
                  </span>
                  <span style={{ color: "var(--text-sub)", fontSize: 13, marginLeft: 10 }}>
                    {item.vendor_name}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 13, flexWrap: "wrap" }}>
                  <span style={{ color: "var(--text-sub)", fontWeight: 600 }}>
                    Expected: {item.expected_delivery_date}
                  </span>
                  <span style={{ color: "var(--danger)", fontWeight: 800 }}>
                    {Math.abs(item.remaining_delivery_days)} days overdue
                  </span>
                  <span style={{ color: "var(--text-sub)", fontWeight: 600 }}>
                    Spec: {item.specification?.slice(0, 50)}…
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}